import { Injectable } from '@angular/core';
import { ModalDialogNotificationService, NotificationType, ServerRootService } from '@se/platform';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, filter, Subject } from 'rxjs';
import { ExtendedSuggestion, Suggestion, SaveExtendedSuggestionEvent, CpControlSuggestions, User } from '@se/ui.text.editor';
import { CpTextEditorControl, PropertyType } from '../../cp-configuration/view-model';
import { TranslateService } from '@ngx-translate/core';
import { Logger } from '@se/se.platform.base';
import { IdDescriptionItem } from '@se/models';

export interface ClinicalDefaultReportResponse {
    id?: string;
    code: string;
    description: string;
    texts: { [key: string]: string };
    isNormality?: boolean;
    modalities: Array<IdDescriptionItem>;
    standardModalities: Array<IdDescriptionItem>;
    groupName?: string;
    referringPhysician?: any;
    groupDescription?: string;
    isStandard?: boolean; //standard senza modulo/metodica
    modules?: Array<IdDescriptionItem>;
    standardModules?: Array<IdDescriptionItem>;
}

export interface ClinicalDefaultReportRequest {
    idReferringPhysician: string;
    groupName: string;
    idExaminations: string[];
}

export interface SelectedSuggestionEvent {
    groupNameHashCode: number;
    suggestion: Suggestion;
    fromDefaultTextName: string;
}

export interface SelectedExtendedSuggestionEvent {
    groupNameHashCode: number;
    suggestion: ExtendedSuggestion;
    fromDefaultTextName: string;
}

@Injectable({
    providedIn: 'root',
})
export class ClinicalDefaultReportService extends ServerRootService {
    private data: Map<number, Observable<Array<Suggestion>>> = new Map<number, Observable<Array<Suggestion>>>();
    private cpSelector: Map<string, CpControlSuggestions> = new Map<string, CpControlSuggestions>();

    private selectedExtendedSuggestion$: Subject<SelectedExtendedSuggestionEvent> = new Subject<SelectedExtendedSuggestionEvent>();
    private selectedExtendedSuggestionStream: Observable<SelectedExtendedSuggestionEvent> = this.selectedExtendedSuggestion$.asObservable();

    private updateSuggestion$: Subject<SaveExtendedSuggestionEvent> = new Subject<SaveExtendedSuggestionEvent>();
    private updateSuggestionStream: Observable<SaveExtendedSuggestionEvent> = this.updateSuggestion$.asObservable();

    constructor(private http: HttpClient,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private translate: TranslateService,
        readonly logger: Logger) {
        super();
    }

    setCpControlSuggestion(control: CpTextEditorControl, model: PropertyType | undefined | null, referringPhysician: User) {
        let cpControlSuggestion: CpControlSuggestions = new CpControlSuggestions(
            control.id,
            control.defaultText ? control.defaultText.defaultTextName : '',
            control.defaultText ? control.defaultText.defaultTextGroupName : '',
            model ? model as string : '',
            null,
            referringPhysician,
            undefined,
            control.name
        )
        this.cpSelector.set(cpControlSuggestion.defaultName, cpControlSuggestion);
    }

    setCpSuggestionHashCode(hashCode: number, defaultTextName: string) {
        let value = this.cpSelector.get(defaultTextName);
        if (value) {
            value.groupHashCode = hashCode;
        }
    }

    getCpControlSuggestion(): Map<string,CpControlSuggestions> {
        return this.cpSelector;
    }

    unregisterCpControlSuggestion() {
        this.cpSelector.clear();
    }

    getSuggestions(request: ClinicalDefaultReportRequest | undefined, label: string): Observable<Array<Suggestion>> | undefined {
        if (!request || !request.groupName) {
            throw new Error(`Request not present`);
        }
        const requestHashCode = this.getRequestHashCode(request);
        if (!this.data.has(requestHashCode)) {
            this.init(request, label);
        }
        return this.data.get(requestHashCode);
    }

    getRequestHashCode(request: ClinicalDefaultReportRequest): number {
        return this.hashCode(JSON.stringify(request));
    }

    private init(request: ClinicalDefaultReportRequest, label: string) {
        if (!request.groupName) {
            throw new Error(`Request for DefaultGroupName without groupName`);
        }
        this.data.set(this.getRequestHashCode(request), this.callServiceAndTransform(request, label));
    }

    private callServiceAndTransform(request: ClinicalDefaultReportRequest, label: string): Observable<Array<Suggestion>> {
        return this.getClinicalDefaultReport(request).pipe(
            map(
                (items: Array<ClinicalDefaultReportResponse>) => {
                    return this.clinicalDefaultReportsToSuggestions(this.getRequestHashCode(request), items, label);
                }),
            catchError(error => {
                this.logger.warn('ClinicalDefaultReportService - impossibile recuperare i dati. ', error);
                this.errorLoadSuggestions();
                return of([]);
            }),
        );
    }

    private errorLoadSuggestions() {
        this.modalDialogNotificationService
            .notify({
                message: this.translate.instant('CannotLoadPredefinite'),
                title: this.translate.instant('Warning'),
                type: NotificationType.error,
                buttonYes: false,
                buttonNo: false,
                buttonOk: true,
                buttonCancel: false,
            });
    }

    getSelectedSuggestion(request: ClinicalDefaultReportRequest, defaultTextName: string): Observable<SelectedExtendedSuggestionEvent> {
        return this.selectedExtendedSuggestionStream.pipe(
            filter((event: SelectedExtendedSuggestionEvent) =>
                event.groupNameHashCode === this.getRequestHashCode(request) &&
                event.fromDefaultTextName !== defaultTextName
            )
        );
    }

    getUpdateSuggestions(request: ClinicalDefaultReportRequest): Observable<SaveExtendedSuggestionEvent> {
        return this.updateSuggestionStream.pipe(
            filter((event: SaveExtendedSuggestionEvent) =>
                event.groupNameHashCode === this.getRequestHashCode(request) 
            )
        );
    }

    updateSuggestions(saveExtendedSuggestionEvent: SaveExtendedSuggestionEvent) {
        this.updateSuggestion$.next(saveExtendedSuggestionEvent);
    }


    saveSuggestion(request: ClinicalDefaultReportResponse): Observable<ClinicalDefaultReportResponse> {
        return this.http.post<ClinicalDefaultReportResponse>(this.getServerRoot() + '/ClinicalDefaultReport/SaveClinicalDefaultReport', request, this.buildOptions());
    }

    unregisterRequest(request: ClinicalDefaultReportRequest) {
        this.data.delete(this.getRequestHashCode(request));
    }

    deleteSuggestion(idClinicalDefaultReports: Array<string>) {
        return this.http.post(this.getServerRoot() + '/ClinicalDefaultReport/DeleteClinicalDefaultReports', idClinicalDefaultReports, this.buildOptions());
    }

    selectSuggestion(extendedSuggestion: ExtendedSuggestion, fromDefaultTextName: string) {
        const event: SelectedExtendedSuggestionEvent = {
            groupNameHashCode: extendedSuggestion.suggestion.groupNameHashCode,
            suggestion: extendedSuggestion,
            fromDefaultTextName: fromDefaultTextName,
        };
        this.selectedExtendedSuggestion$.next(event);
    }

    private getClinicalDefaultReport(request: ClinicalDefaultReportRequest): Observable<Array<ClinicalDefaultReportResponse>> {
        return this.http.post<Array<ClinicalDefaultReportResponse>>(this.getServerRoot() + '/ClinicalDefaultReport/GetClinicalDefaultReports', request, this.buildOptions());
    }

    private clinicalDefaultReportsToSuggestions(groupNameHashCode: number, items: Array<ClinicalDefaultReportResponse>, label: string): Array<Suggestion> {
        let idCount: number = 0;
        return items.map((item: ClinicalDefaultReportResponse) => {

            return this.clinicalDefaultReportToSuggestion(groupNameHashCode, idCount++, item, label);
        });
    }

    private clinicalDefaultReportToSuggestion(groupNameHashCode: number, id: number, item: ClinicalDefaultReportResponse, label: string): Suggestion {
        return {
            id: id,
            guid: item.id ? item.id : '',
            Description: item.description,
            Sentence: item.texts,
            IsNormality: item.isNormality ?? false,
            code: item.code,
            newLineAfter: false,
            groupNameHashCode: groupNameHashCode,
            modalities: item.modalities,
            standardModalities: item.standardModalities,
            modules: item.modules ?? [],
            standardModules: item.standardModules ?? [],
            isStandard: item.isStandard ?? false,
            groupName: item.groupName,
            referringPhysician: item.referringPhysician,
            label: label ?? ''
        };
    }

    private hashCode(value: string): number {
        var hash = 0,
            i, chr;
        if (value.length === 0) return hash;
        for (i = 0; i < value.length; i++) {
            chr = value.charCodeAt(i);
            hash = ((hash << 5) - hash) + chr;
            hash |= 0; // Convert to 32bit integer
        }
        return hash;
    }
}
